import React, { useState, useRef, useEffect } from 'react';
import './VerificationCode.css';

interface VerificationCodeProps {
  email: string;
  onVerificationSuccess: (userData: any) => void;
  onBack: () => void;
  onResendCode: () => void;
}

const VerificationCode: React.FC<VerificationCodeProps> = ({
  email,
  onVerificationSuccess,
  onBack,
  onResendCode
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown logic
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // Handle verification code input
  const handleCodeChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto focus to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto verify when 6 digits are filled
    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      handleVerifyCode(newCode.join(''));
    }
  };

  // Handle keyboard events
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // If current input is empty, delete previous input content
        const newCode = [...code];
        newCode[index - 1] = '';
        setCode(newCode);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError('');
      handleVerifyCode(pastedData);
    }
  };

  // Verify verification code
  const handleVerifyCode = async (verificationCode: string) => {
    setIsLoading(true);
    setError('');

    try {
      // Call backend API to verify code
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          code: verificationCode,
        }),
      });
      const data = await response.json();
      if (response.ok && data.token) {
        // 传递完整的用户数据给父组件
        onVerificationSuccess(data);
      } else {
        setError(data.message || 'Verification code error, please try again');
        // Clear verification code
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error('Verification failed:', error);
      setError('Verification failed, please check network connection');
      // Clear verification code
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification code
  const handleResend = async () => {
    if (!canResend) return;

    setCanResend(false);
    setCountdown(60);
    setError('');
    
    try {
      await onResendCode();
    } catch (error) {
      setError('Send failed, please try again later');
      setCanResend(true);
      setCountdown(0);
    }
  };

  // Manual verify button click
  const handleManualVerify = () => {
    const verificationCode = code.join('');
    if (verificationCode.length === 6) {
      handleVerifyCode(verificationCode);
    } else {
      setError('Please enter complete 6-digit verification code');
    }
  };

  return (
    <div className="verification-code">
      <div className="verification-header">
        <button className="back-button" onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h2 className="verification-title">Verify Your Email</h2>
      </div>

      <div className="verification-content">
        <p className="verification-description">
          We've sent a 6-digit verification code to <strong>{email}</strong>
        </p>

        <div className="code-input-container">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(ref) => (inputRefs.current[index] = ref)}
              type="text"
              className={`code-input ${error ? 'error' : ''}`}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              maxLength={1}
              disabled={isLoading}
            />
          ))}
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <div className="verification-actions">
          <button
            className={`verify-button ${isLoading ? 'loading' : ''}`}
            onClick={handleManualVerify}
            disabled={isLoading || code.some(digit => digit === '')}
          >
            {isLoading ? (
              <div className="loading-spinner"></div>
            ) : (
              'Verify'
            )}
          </button>

          <div className="resend-section">
            {canResend ? (
              <button className="resend-button" onClick={handleResend}>
                Resend Code
              </button>
            ) : (
              <span className="countdown-text">
                Resend in {countdown}s
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationCode;
